import { Copy, Lock, MoreVertical, Pencil, Trash2, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PRESET_LABELS, type PresetCode, type RoleSummary } from '@/pages/company/roles/rolesLogic';

interface RoleCardProps {
  role: RoleSummary;
  /** company_admin (иначе действия скрыты). */
  editable?: boolean;
  onOpen: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
}

function memberWord(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'участник';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'участника';
  return 'участников';
}

/**
 * RoleCard — карточка роли в списке (фрейм 1, AK-343).
 *
 * Системная: замочек + бейдж «Системная», действия «Открыть» (read-only) +
 * «Дублировать». Кастомная: бейдж «Кастомная» (+ «из пресета» если preset_code),
 * действия «Править» + меню (удалить). Счётчик участников — из member_count.
 */
export function RoleCard({
  role,
  editable = false,
  onOpen,
  onEdit,
  onDelete,
  onDuplicate,
}: RoleCardProps) {
  const isSystem = role.is_system;
  const presetLabel = role.preset_code
    ? PRESET_LABELS[role.preset_code as PresetCode]?.name
    : null;

  return (
    <div
      className={
        'flex items-center gap-4 rounded-xl border border-border p-3.5 ' +
        (isSystem ? 'bg-secondary/40' : 'bg-card')
      }
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate font-semibold text-foreground">{role.name}</span>
          {isSystem ? (
            <Badge variant="secondary" className="gap-1">
              <Lock className="h-3 w-3" /> Системная
            </Badge>
          ) : (
            <>
              <Badge variant="outline" className="border-accent-brand/40 text-accent-brand">
                Кастомная
              </Badge>
              {presetLabel && (
                <Badge variant="secondary" className="font-normal">
                  из «{presetLabel}»
                </Badge>
              )}
            </>
          )}
        </div>
        {role.description && (
          <p className="mt-0.5 truncate text-[12.5px] text-muted-foreground">
            {role.description}
          </p>
        )}
      </div>

      <div className="flex flex-none items-center gap-1.5 whitespace-nowrap text-xs text-muted-foreground">
        <Users className="h-3.5 w-3.5" />
        <span className="tabular-nums">
          <b className="text-foreground">{role.member_count}</b> {memberWord(role.member_count)}
        </span>
      </div>

      <div className="flex flex-none items-center gap-1.5">
        {isSystem ? (
          <>
            <Button variant="outline" size="sm" onClick={onOpen}>
              Открыть
            </Button>
            {editable !== false && onDuplicate && (
              <Button variant="ghost" size="sm" onClick={onDuplicate}>
                <Copy className="h-4 w-4" />
                <span className="hidden sm:inline">Дублировать</span>
              </Button>
            )}
          </>
        ) : editable ? (
          <>
            <Button variant="outline" size="sm" onClick={onEdit}>
              <Pencil className="h-4 w-4" />
              <span className="hidden sm:inline">Править</span>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Ещё">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={onEdit}>
                  <Pencil className="h-4 w-4" /> Переименовать / права
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={onDelete}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4" /> Удалить
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        ) : (
          <Button variant="outline" size="sm" onClick={onOpen}>
            Открыть
          </Button>
        )}
      </div>
    </div>
  );
}
